import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { usersAPI, officeLocationsAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Card, CardContent } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { toast } from "sonner";
import {
  Users,
  Search,
  Edit,
  ArrowLeft,
  MapPin,
} from "lucide-react";
import { ROLE_LABELS } from "../../../shared/lib/utils";

export default function UsersManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [officeFilter, setOfficeFilter] = useState("all");

  // Form state
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedOffice, setSelectedOffice] = useState("");

  // Bulk selection
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkOffice, setBulkOffice] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    loadData();
  }, [roleFilter, officeFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (officeFilter !== "all") params.office_location_id = officeFilter;

      const [usersRes, officesRes] = await Promise.all([
        usersAPI.getAll(params),
        officeLocationsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setOffices(officesRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(query) ||
      u.employee_id.toLowerCase().includes(query) ||
      (u.email && u.email.toLowerCase().includes(query))
    );
  });

  const openEditDialog = (targetUser) => {
    setEditingUser(targetUser);
    setSelectedRole(targetUser.role);
    setSelectedOffice(targetUser.office_location_id || "none");
    setDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await usersAPI.update(editingUser.id, {
        role: selectedRole,
        office_location_id: selectedOffice === "none" ? null : selectedOffice,
      });
      toast.success("User updated");
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Select users first");
      return;
    }
    if (!bulkOffice || bulkOffice === "none") {
      toast.error("Select an office location");
      return;
    }

    setSubmitting(true);
    try {
      await usersAPI.bulkUpdateOffice({
        user_ids: selectedUsers,
        office_location_id: bulkOffice,
      });
      toast.success(`Updated ${selectedUsers.length} users`);
      setBulkDialogOpen(false);
      setSelectedUsers([]);
      setBulkOffice("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to bulk update");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="users-manager-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="font-mono text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-black text-2xl flex items-center gap-2">
            <Users className="h-6 w-6 text-[#FF6B00]" />
            User Management
          </h1>
        </div>

        {selectedUsers.length > 0 && (
          <Button
            onClick={() => setBulkDialogOpen(true)}
            className="h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
            data-testid="bulk-update-btn"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Assign Office ({selectedUsers.length})
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-2 border-border rounded-none mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  className="h-10 rounded-none border-2 font-mono pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Role
              </Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-10 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="worker">Worker</SelectItem>
                  <SelectItem value="crew_leader">Crew Leader</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Office
              </Label>
              <Select value={officeFilter} onValueChange={setOfficeFilter}>
                <SelectTrigger className="h-10 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Offices</SelectItem>
                  {offices.filter(o => o.status === "active").map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.office_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <div className="font-mono text-sm text-muted-foreground">
                {filteredUsers.length} user(s)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Select All */}
      <div className="flex items-center gap-3 mb-4">
        <Checkbox
          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
          onCheckedChange={toggleSelectAll}
          className="h-5 w-5 rounded-none"
        />
        <span className="font-mono text-sm">Select All</span>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.map((targetUser) => (
          <Card
            key={targetUser.id}
            className={`border-2 rounded-none ${
              selectedUsers.includes(targetUser.id)
                ? "border-[#FF6B00] bg-[#FF6B00]/5"
                : "border-border"
            }`}
            data-testid={`user-item-${targetUser.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Checkbox
                  checked={selectedUsers.includes(targetUser.id)}
                  onCheckedChange={() => toggleUserSelection(targetUser.id)}
                  className="h-5 w-5 rounded-none"
                />

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-bold">
                      {targetUser.name}
                    </span>
                    <Badge variant="outline" className="rounded-none font-mono text-xs">
                      {targetUser.employee_id}
                    </Badge>
                    <Badge
                      className="rounded-none font-mono text-xs bg-slate-500 text-white"
                    >
                      {ROLE_LABELS[targetUser.role] || targetUser.role}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {targetUser.email && <span>{targetUser.email}</span>}
                    {targetUser.office_location_name ? (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {targetUser.office_location_name}
                      </span>
                    ) : (
                      <span className="text-amber-500">No office assigned</span>
                    )}
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(targetUser)}
                  className="rounded-none border-2 font-mono text-xs"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-2 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Edit User: {editingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Role
              </Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="h-12 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="worker">Substation Worker</SelectItem>
                  <SelectItem value="crew_leader">Crew Leader</SelectItem>
                  <SelectItem value="operator">Area Operator</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Office Location
              </Label>
              <Select value={selectedOffice} onValueChange={setSelectedOffice}>
                <SelectTrigger className="h-12 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">No Office Assigned</SelectItem>
                  {offices.filter(o => o.status === "active").map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.office_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
            >
              {submitting ? <span className="spinner" /> : "Update User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="rounded-none border-2 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Bulk Assign Office Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assign office location to {selectedUsers.length} selected user(s)
            </p>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Office Location
              </Label>
              <Select value={bulkOffice} onValueChange={setBulkOffice}>
                <SelectTrigger className="h-12 rounded-none border-2 font-mono">
                  <SelectValue placeholder="Select office..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {offices.filter(o => o.status === "active").map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.office_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleBulkUpdate}
              disabled={submitting || !bulkOffice}
              className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
            >
              {submitting ? <span className="spinner" /> : "Assign to Selected Users"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
